import React, { useEffect, useId, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { alpha } from "@mui/material/styles";

const normalize = (value) =>
  String(value || "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

const searchableParts = (column) => [
  column.id,
  column.label,
  column.description,
  ...(column.details || []).flatMap((detail) => [detail.label, detail.text]),
  column.example,
  column.note,
  ...(column.keywords || []),
];

/**
 * Searchable column reference. Summaries are always visible; details expand inline.
 * columns: [{ id, label, description, details?: [{ label, text }], example?, note?, keywords?: string[] }]
 * Change contextKey when the schedule or view changes to reset the search.
 */
const ColumnGuideDialog = ({
  open,
  onClose,
  title = "Column guide",
  description,
  columns = [],
  contextKey,
}) => {
  const guideId = useId();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const normalizedQuery = normalize(query);
  const terms = normalizedQuery.split(" ").filter(Boolean);
  const results = columns
    .map((column, index) => {
      const label = normalize(column.label);
      const content = normalize(searchableParts(column).join(" "));
      return {
        column,
        index,
        matches: terms.every((term) => (term.length <= 2 ? content.split(" ").includes(term) : content.includes(term))),
        rank: label === normalizedQuery ? 0 : terms.every((term) => label.includes(term)) ? 1 : 2,
      };
    })
    .filter((result) => result.matches)
    .sort((a, b) => (terms.length ? a.rank - b.rank : 0) || a.index - b.index);

  useEffect(() => {
    if (open) {
      setQuery("");
      setExpanded({});
      if (resultsRef.current) resultsRef.current.scrollTop = 0;
    }
  }, [open, contextKey]);

  const updateQuery = (value) => {
    setQuery(value);
    setExpanded({});
    if (resultsRef.current) resultsRef.current.scrollTop = 0;
  };

  const clearSearch = () => {
    updateQuery("");
    searchRef.current?.focus();
  };

  const highlight = (value) => {
    if (!value || !terms.length || typeof value !== "string") return value;
    // Search terms contain only letters and numbers, so they are safe in a RegExp.
    const pattern = new RegExp(`(${[...terms].sort((a, b) => b.length - a.length).join("|")})`, "giu");
    return value.split(pattern).map((part, index) =>
      index % 2 ? (
        <Box
          component='mark'
          key={index}
          sx={{ color: "inherit", bgcolor: (theme) => alpha(theme.palette.warning.main, 0.25), borderRadius: 0.5 }}
        >
          {part}
        </Box>
      ) : part
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      scroll='paper'
      aria-labelledby={`${guideId}-title`}
      aria-describedby={description ? `${guideId}-description` : undefined}
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
          maxWidth: 880,
          height: 720,
          maxHeight: { xs: "calc(100dvh - 16px)", sm: "calc(100dvh - 32px)" },
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle component='div' sx={{ display: "flex", gap: 1.5, p: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexShrink: 0 }}>
        <Box sx={{ display: { xs: "none", sm: "grid" }, placeItems: "center", width: 40, height: 40, flexShrink: 0, borderRadius: 2, color: "primary.main", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}>
          <HelpOutlineIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={`${guideId}-title`} component='h2' variant='h6' fontWeight={700}>
            {title}
          </Typography>
          {description && (
            <Typography id={`${guideId}-description`} variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Tooltip title='Close column guide'>
          <IconButton onClick={onClose} aria-label='Close column guide' size='small' sx={{ alignSelf: "flex-start" }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2, flexShrink: 0 }}>
        <TextField
          autoFocus
          fullWidth
          inputRef={searchRef}
          label='Search columns'
          placeholder='Enter a column name or keyword'
          value={query}
          onChange={(event) => updateQuery(event.target.value)}
          inputProps={{ 'aria-controls': `${guideId}-results` }}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchIcon color='action' /></InputAdornment>,
            endAdornment: query ? (
              <InputAdornment position='end'>
                <IconButton aria-label='Clear search' onClick={clearSearch} edge='end' size='small'>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Typography variant='caption' color='text.secondary' sx={{ display: "block", mt: 1 }}>
          Search names, meanings, examples and notes. Expand a column for more detail.
        </Typography>
        <Typography role='status' aria-live='polite' variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
          {terms.length ? `${results.length} of ${columns.length} columns found` : `${columns.length} columns · Browse all meanings`}
        </Typography>
      </Box>
      <DialogContent
        dividers
        ref={resultsRef}
        id={`${guideId}-results`}
        sx={{ p: { xs: 2, sm: 3 }, minHeight: 0, bgcolor: "background.default", overflowY: "auto", overflowWrap: "anywhere", scrollbarGutter: "stable" }}
      >
        {results.map(({ column, index }) => {
          const hasDetails = Boolean(column.details?.length || column.example || column.note);
          const summary = (
            <Box sx={{ minWidth: 0 }}>
              <Typography component='h3' variant='subtitle1' fontWeight={700}>
                {highlight(column.label)}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5, lineHeight: 1.7 }}>
                {highlight(column.description)}
              </Typography>
            </Box>
          );
          const cardStyle = { mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: "background.paper", boxShadow: "none" };
          if (!hasDetails) return <Box key={column.id} component='article' sx={{ ...cardStyle, p: 2 }}>{summary}</Box>;
          return (
            <Accordion
              key={column.id}
              disableGutters
              expanded={expanded[column.id] ?? Boolean(terms.length)}
              onChange={(_event, isExpanded) => setExpanded((previous) => ({ ...previous, [column.id]: isExpanded }))}
              sx={{ ...cardStyle, '&:before': { display: "none" }, '&:first-of-type, &:last-of-type': { borderRadius: "12px" } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id={`${guideId}-summary-${index}`}
                aria-controls={`${guideId}-details-${index}`}
                sx={{ px: 2, '& .MuiAccordionSummary-content': { my: 2, mr: 1.5 }, '&.Mui-focusVisible': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) } }}
              >
                {summary}
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
                  <Box component='dl' sx={{ m: 0 }}>
                    {(column.details || []).map((detail, detailIndex) => (
                      <Box key={detailIndex} sx={{ '& + &': { mt: 2 } }}>
                        {detail.label && <Typography component='dt' variant='body2' fontWeight={700}>{highlight(detail.label)}</Typography>}
                        <Typography component='dd' variant='body2' color='text.secondary' sx={{ m: 0, mt: 0.5, lineHeight: 1.7 }}>{highlight(detail.text)}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {column.example && (
                    <Box sx={{ mt: column.details?.length ? 2 : 0, p: 2, borderRadius: 2, bgcolor: "background.default" }}>
                      <Typography variant='body2' fontWeight={700} sx={{ mb: 0.5 }}>Example</Typography>
                      <Typography variant='body2' sx={{ lineHeight: 1.7 }}>{highlight(column.example)}</Typography>
                    </Box>
                  )}
                  {column.note && <Alert severity='info' sx={{ mt: column.details?.length || column.example ? 2 : 0 }}>{highlight(column.note)}</Alert>}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
        {!results.length && (
          <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
            <SearchIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
            <Typography component='h3' variant='subtitle1' fontWeight={700}>
              {columns.length ? "No matching columns" : "No column descriptions available"}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              {columns.length ? "Try a shorter keyword, a column name or a term from its description." : "There are no column descriptions for this view yet."}
            </Typography>
            {query && <Button onClick={clearSearch} sx={{ mt: 2, textTransform: "none" }}>Clear search</Button>}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ColumnGuideDialog;
